import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/theme/ThemeProvider";
import ThemeToggle from "@/components/ThemeToggle";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CoachDashboard from "@/pages/coach/Dashboard";
import CoachStudents from "@/pages/coach/Students";
import CoachStudentDetail from "@/pages/coach/StudentDetail";
import CoachCalendar from "@/pages/coach/Calendar";
import CoachLessonDetail from "@/pages/coach/LessonDetail";
import CoachMessages from "@/pages/coach/Messages";
import CoachAnalytics from "@/pages/coach/Analytics";
import CoachPlanDraft from "@/pages/coach/PlanDraft";
import CoachPlansLibrary from "@/pages/coach/PlansLibrary";
import CoachCapture from "@/pages/coach/Capture";
import { CaptureList as CoachCaptureList, CaptureDetail as CoachCaptureDetail } from "@/pages/coach/CaptureSessions";
import CoachAvailability from "@/pages/coach/Availability";
import StudentDashboard from "@/pages/student/Dashboard";
import StudentBook from "@/pages/student/Book";
import StudentLessons from "@/pages/student/Lessons";
import StudentLessonDetail from "@/pages/student/LessonDetail";
import StudentMessages from "@/pages/student/Messages";
import StudentProfile from "@/pages/student/Profile";
import CoachProfile from "@/pages/coach/Profile";
import AdminDashboard from "@/pages/admin/Dashboard";
import Help from "@/pages/Help";
import Ask from "@/pages/Ask";
import PublicPlan from "@/pages/PublicPlan";
import PublicCapture from "@/pages/PublicCapture";
import StudentCaptures from "@/pages/student/Captures";
import StudentCaptureDetail from "@/pages/student/CaptureDetail";

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user && user.role === "coach") return <Navigate to="/coach" replace />;
  if (user && user.role === "student") return <Navigate to="/student" replace />;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <PushNavHandler />
            <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/p/:token" element={<PublicPlan />} />
            {/* Public, no-auth view of a shared motion-capture analysis. The
                token is the only handle a visitor holds, so this sits alongside
                the other public routes rather than behind ProtectedRoute. */}
            <Route path="/c/:token" element={<PublicCapture />} />

            <Route
              element={
                <ProtectedRoute role="coach">
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/coach" element={<CoachDashboard />} />
              <Route path="/coach/students" element={<CoachStudents />} />
              <Route path="/coach/students/:id" element={<CoachStudentDetail />} />
              <Route path="/coach/calendar" element={<CoachCalendar />} />
              <Route path="/coach/availability" element={<CoachAvailability />} />
              <Route path="/coach/plans" element={<CoachPlanDraft />} />
              <Route path="/coach/plans/library" element={<CoachPlansLibrary />} />
              <Route path="/coach/capture" element={<CoachCaptureList />} />
              <Route path="/coach/capture/new" element={<CoachCapture />} />
              <Route path="/coach/capture/:id" element={<CoachCaptureDetail />} />
              <Route path="/coach/lessons/:id" element={<CoachLessonDetail />} />
              <Route path="/coach/messages" element={<CoachMessages />} />
              <Route path="/coach/analytics" element={<CoachAnalytics />} />
              <Route path="/coach/ask" element={<Ask />} />
              <Route path="/coach/help" element={<Help />} />
              <Route path="/coach/profile" element={<CoachProfile />} />
            </Route>

            <Route
              element={
                <ProtectedRoute role="student">
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/student" element={<StudentDashboard />} />
              <Route path="/student/book" element={<StudentBook />} />
              <Route path="/student/lessons" element={<StudentLessons />} />
              <Route path="/student/lessons/:id" element={<StudentLessonDetail />} />
              {/* Motion-capture analyses shared with this student. These paths
                  are exactly what the capture notification + push payload link
                  to — before they existed, those links 404'd and the athlete
                  was bounced to the dashboard with no way to find the analysis. */}
              <Route path="/student/captures" element={<StudentCaptures />} />
              <Route path="/student/captures/:id" element={<StudentCaptureDetail />} />
              <Route path="/student/messages" element={<StudentMessages />} />
              <Route path="/student/profile" element={<StudentProfile />} />
              <Route path="/student/ask" element={<Ask />} />
              <Route path="/student/help" element={<Help />} />
            </Route>

            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <ThemedToaster />
          <ThemeToggle />
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

// Sonner needs the explicit theme prop, not just CSS variables — this
// small wrapper reads from ThemeProvider so toasts flip colors too.
function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} />;
}

// Bridges service-worker notificationclick postMessages into react-router
// so tapping a push notification lands the user on the correct in-app page.
function PushNavHandler() {
  const navigate = useNavigate();
  React.useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const onMsg = (event) => {
      if (event.data?.type === "navigate" && typeof event.data.url === "string") {
        // Only intercept in-app paths; leave external URLs alone
        if (event.data.url.startsWith("/")) navigate(event.data.url);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [navigate]);
  return null;
}

export default App;
